import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Alert, AlertStatus, AlertToken } from "../../redux/alerts";
import { IAppState } from "../../redux/types";
import { Dispatcher } from "../../service/RootDispather";
import { AvatarResidentUser } from "../common/AvatarUser";
import {
  OverflowTip,
  RoomBedDisplayByMin,
  processAlertFeedback,
} from "../common/Common";
// import { FeedbackModalSkeleton } from '../../common/skeleton/Skeletons';
// import "./styles.scss";
import { Skeleton } from "@material-ui/lab";
import { residentsCreator } from "../../redux/residents/selectors";
import { alertIconToken } from "../../utils/AlertTo";
import { ModalTypes } from "../status/Callbacks";

const AlertFeedback = ({
  alert,
  callback,
}: {
  alert: Alert;
  callback: (...params) => void;
}): JSX.Element => {
  const dispatcher = Dispatcher();
  const tStatus = useTranslation("status").t;
  const tCommon = useTranslation("common").t;
  const {
    isLoading,
    feedback,
    priority,
    generated,
    lastUpdate,
    isSuccessfulResolve,
    isGetFeedbackSuccessful,
  } = useSelector((state: IAppState) => state.feedback);
  const [listItemSelected, setlistItemSelected] = useState(false);
  const [resultFeedback, setResultFeedback] = useState(feedback);
  const [submitted, setSubmitted] = useState(false);
  const title = tCommon("alerts." + alert?.alertToken);

  useEffect(() => {
    dispatcher.getFeedbackForAlert({
      residentId: alert.patientId,
      alertType: alert.alertToken,
    });
  }, [dispatcher, alert]);

  useEffect(() => {
    setResultFeedback(feedback);
  }, [feedback]);

  useEffect(() => {
    if (callback && isSuccessfulResolve && submitted) callback(ModalTypes.BACK);
    if (isSuccessfulResolve === false && submitted) setSubmitted(false);
  }, [isSuccessfulResolve, submitted, callback]);

  useEffect(() => {
    if (callback && isGetFeedbackSuccessful === false)
      callback(ModalTypes.BACK);
  }, [isGetFeedbackSuccessful, callback]);

  const onCloseHandler = useCallback(() => {
    setSubmitted(true);
    dispatcher.resolveAlert({
      alertToken: alert.alertToken,
      residentId: alert.patientId,
      alert: {
        generated: generated,
        lastUpdate: lastUpdate,
        priority: priority,
        status: AlertStatus.done,
        feedback: null,
      },
    });
  }, [dispatcher, alert, generated, lastUpdate, priority]);

  const onSubmitHandler = useCallback(() => {
    if (!listItemSelected) return;
    setSubmitted(true);
    dispatcher.resolveAlert({
      alertToken: alert.alertToken,
      residentId: alert.patientId,
      alert: {
        generated: generated,
        lastUpdate: lastUpdate,
        priority: priority,
        status: AlertStatus.done,
        feedback: { ...resultFeedback, comment: "" },
      },
    });
    callback(ModalTypes.BACK);
  }, [
    dispatcher,
    alert,
    generated,
    lastUpdate,
    priority,
    listItemSelected,
    resultFeedback,
    callback,
  ]);
  const onSelect = useCallback(
    (id: string) => {
      const copy = { ...resultFeedback };
      if (Object.keys(resultFeedback.radioButtons).length > 0) {
        Object.keys(resultFeedback.radioButtons).map((key) => {
          copy.radioButtons[key] = key === id ? true : false;
          return null;
        });
      }
      if (Object.keys(resultFeedback.checkBoxes).length > 0) {
        copy.checkBoxes[id] = true;
      }
      setResultFeedback(copy);
      setlistItemSelected(true);
    },
    [resultFeedback]
  );

  const ResidentSelection = useMemo(() => residentsCreator(), []);
  const resident = useSelector((state: IAppState) =>
    ResidentSelection(state, alert?.patientId)
  );
  // const handlers = useSwipeable({
  //   onSwipedRight: () => onCloseHandler(),
  // });
  return (
    <div className={`modal-sm feedback-modal `}>
      <div className="flex-column flex-column-24">
        <div className="flex-center flex-column">
          <div className="flex-gap  flex-gap-8">
            <AvatarResidentUser profile={resident} size={"24"} />
            <RoomBedDisplayByMin
              devicesMinimized={resident?.devicesMinimized}
            />
          </div>
          <OverflowTip
            text={`${resident.firstName} ${resident.lastName}`}
            className="center"
          />
          <span>{tStatus("feedback")}</span>
        </div>
        <div
          className="flex-gap flex-gap-8"
          style={{ justifyContent: "center" }}
        >
          <div className="alert-32">{alertIconToken(alert?.alertToken)}</div>

          <span className="heebo-m">{title}</span>
        </div>
        {isLoading ? (
          <FeedbackModalSkeleton token={alert?.alertToken} />
        ) : (
          <>
            <div className="list-wrap" style={{ marginTop: "10px" }}>
              {feedback &&
              Object.keys(feedback.checkBoxes).length > 0 ? null : feedback &&
                Object.keys(feedback.radioButtons).length > 0 ? (
                <ul className="feedback-list">
                  {Object.keys(feedback.radioButtons).map((key: string) => {
                    return (
                      <li
                        key={key}
                        className={`${
                          resultFeedback?.radioButtons[key] ? "selected" : ""
                        } `}
                        onClick={() => onSelect(key)}
                      >
                        <div className={"radio"}>
                          <input type={"radio"} id={key} name="feedback" />
                          <label htmlFor={key}>
                            {(alert?.alertToken === AlertToken.InBed ||
                              alert?.alertToken === AlertToken.OutOfBed ||
                              alert?.alertToken === AlertToken.AwayFromBed) && (
                              <i
                                className={`icon-feedback-${
                                  key.indexOf("true") !== -1
                                    ? "done"
                                    : "refused"
                                }`}
                                style={{
                                  paddingRight: "4px",
                                  verticalAlign: "middle",
                                }}
                              />
                            )}
                            {tStatus(processAlertFeedback(key))}.
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </>
        )}
        <div
          className="flex-column flex-column-8"
          style={{ marginTop: "10px" }}
        >
          <button
            disabled={!listItemSelected}
            className={"btn primary with-icon full-width save-view"}
            type="submit"
            onClick={onSubmitHandler}
          >
            <i className="icon-feedback-done" />
            {tCommon("buttons.Save")}
          </button>
          <button
            style={{ marginTop: "10px" }}
            className="btn with-icon full-width save-view"
            onClick={onCloseHandler}
            type="button"
          >
            <i className="icon-close" />
            {tStatus("noFeedback")}
          </button>
        </div>
      </div>
    </div>
  );
};

const FeedbackModalSkeleton = ({
  token,
}: {
  token: AlertToken;
}): JSX.Element => {
  const rows = new Array(
    token === AlertToken.Fall ? 4 : token === AlertToken.FallDetected ? 2 : 3
  ).fill(undefined);
  return (
    <div className="list-wrap" style={{ marginTop: "10px" }}>
      {rows.map((el, index) => (
        <React.Fragment key={index + "0"}>
          <Skeleton
            variant="rect"
            width={"100%"}
            key={index + "1"}
            height={20}
            style={{ borderRadius: "2px", marginBottom: "16px" }}
          />
          {index !== rows.length - 1 && (
            <Skeleton
              variant="rect"
              key={index + "2"}
              width={"80%"}
              height={20}
              style={{ borderRadius: "2px", marginBottom: "16px" }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
export default React.memo(AlertFeedback);
